import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PriceSource, PriceMode } from "./priceSource";

// Constants over magic numbers
const TICK_INTERVAL_MS = 1000; // how often we evaluate future window
const MIN_TIME_WINDOW_SEC = 4; // show 4–7 seconds earlier than the event
const MAX_TIME_WINDOW_SEC = 7;
const MIN_GAP_BETWEEN_NEWS_SEC = 7; // avoid spam
const MAX_GAP_WITHOUT_NEWS_SEC = 8; // ensure periodic news even without spikes
const SPIKE_THRESHOLD_PCT = 0.8; // visible spike threshold (more relaxed)
const SCROLL_DURATION_MS = 9000; // ticker scroll duration per message

type Direction = "up" | "down";

interface QuoteTemplate {
  readonly text: string;
  readonly direction: Direction;
  readonly confidencePct: number; // 60..100
}

interface NewsMessage {
  readonly id: string;
  readonly name: string;
  readonly text: string;
  readonly direction: Direction;
  readonly confidencePct: number;
}

// Reporters / influencers pool (generated names)
const REPORTERS: readonly string[] = [
  "Enol Mask", // required
  "Kathy Woods",
  "CZed",
  "Vita Butter",
  "Sam Boneman",
  "Roaring Doge",
  "The Crypto Oracle",
  "Alpha Hamster",
  "Whale Whisperer",
  "Satoshi-ish",
];

// Quote templates — direction-specific, various confidence tones
const QUOTES: readonly QuoteTemplate[] = [
  // Up
  { text: "To the moooooon", direction: "up", confidencePct: 100 }, // required
  {
    text: "I have {c}% certainty we pop up",
    direction: "up",
    confidencePct: 90,
  },
  {
    text: "Signals scream green — {c}% odds",
    direction: "up",
    confidencePct: 85,
  },
  { text: "Feeling bullish, about {c}%", direction: "up", confidencePct: 70 },
  {
    text: "Wind at our backs ({c}% chance)",
    direction: "up",
    confidencePct: 65,
  },
  {
    text: "My hamster senses: rocket soon ({c}%)",
    direction: "up",
    confidencePct: 60,
  },
  // Down
  {
    text: "Brace for impact — {c}% drop likely",
    direction: "down",
    confidencePct: 90,
  },
  {
    text: "Red ahead with {c}% probability",
    direction: "down",
    confidencePct: 80,
  },
  { text: "Gravity wins ({c}% chance)", direction: "down", confidencePct: 70 },
  {
    text: "Whales dumping? I'd say {c}%",
    direction: "down",
    confidencePct: 65,
  },
  {
    text: "Clouds gather — {c}% odds we dip",
    direction: "down",
    confidencePct: 60,
  },
];

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function formatQuoteText(tpl: QuoteTemplate): string {
  return tpl.text.replace("{c}", String(tpl.confidencePct));
}

function detectSpike(
  currentPrice: number,
  future: ReadonlyArray<number>,
  pointsPerSecond: number
): {
  direction: Direction;
  magnitudePct: number;
  occursWithinSec: number;
} | null {
  if (!isFinite(currentPrice) || future.length === 0) return null;
  const maxLookaheadPts = Math.min(
    future.length,
    Math.ceil(MAX_TIME_WINDOW_SEC * pointsPerSecond)
  );
  const minPts = Math.max(1, Math.floor(MIN_TIME_WINDOW_SEC * pointsPerSecond));
  let maxUpPct = 0;
  let maxUpIndex = -1;
  let maxDownPct = 0;
  let maxDownIndex = -1;
  for (let i = 0; i < maxLookaheadPts; i++) {
    const price = future[i];
    if (!isFinite(price)) continue;
    const deltaPct =
      ((price - currentPrice) / Math.max(1e-9, currentPrice)) * 100;
    if (deltaPct > maxUpPct) {
      maxUpPct = deltaPct;
      maxUpIndex = i;
    }
    if (-deltaPct > maxDownPct) {
      maxDownPct = -deltaPct; // store as positive magnitude
      maxDownIndex = i;
    }
  }
  const bestUpSec =
    maxUpIndex >= 0 ? maxUpIndex / Math.max(1, pointsPerSecond) : Infinity;
  const bestDownSec =
    maxDownIndex >= 0 ? maxDownIndex / Math.max(1, pointsPerSecond) : Infinity;

  // Ensure spike is within 2..5 seconds window and above threshold
  const upValid =
    maxUpPct >= SPIKE_THRESHOLD_PCT &&
    bestUpSec >= MIN_TIME_WINDOW_SEC &&
    bestUpSec <= MAX_TIME_WINDOW_SEC;
  const downValid =
    maxDownPct >= SPIKE_THRESHOLD_PCT &&
    bestDownSec >= MIN_TIME_WINDOW_SEC &&
    bestDownSec <= MAX_TIME_WINDOW_SEC;

  if (!upValid && !downValid) return null;
  if (upValid && (!downValid || maxUpPct >= maxDownPct))
    return {
      direction: "up",
      magnitudePct: maxUpPct,
      occursWithinSec: bestUpSec,
    };
  if (downValid)
    return {
      direction: "down",
      magnitudePct: maxDownPct,
      occursWithinSec: bestDownSec,
    };
  return null;
}

function buildNewsMessage(predicted: Direction): NewsMessage {
  // Pick a quote whose declared direction matches the message we will show
  const candidates = QUOTES.filter((q) => q.direction === predicted);
  const chosen = pickRandom(candidates);

  // Choose a reporter; ensure Enol Mask appears sometimes but not always
  const name = Math.random() < 0.2 ? "Enol Mask" : pickRandom(REPORTERS);

  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    name,
    text: formatQuoteText(chosen),
    direction: chosen.direction,
    confidencePct: chosen.confidencePct,
  };
}

export interface NewsTickerProps {
  readonly enabled: boolean;
  readonly priceSource: PriceSource | null;
  readonly pointsPerSecond: number;
  readonly currentPrice: number;
  readonly mode: PriceMode;
}

export function NewsTicker({
  enabled,
  priceSource,
  pointsPerSecond,
  currentPrice,
  mode,
}: NewsTickerProps) {
  const [message, setMessage] = useState<NewsMessage | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const lastShownAtRef = useRef<number>(0);
  const currentPriceRef = useRef<number>(currentPrice);

  useEffect(() => {
    currentPriceRef.current = currentPrice;
  }, [currentPrice]);

  const isActive = enabled && (mode === "real" || mode === "pvp");

  const canPeek = useMemo(() => {
    return Boolean(
      priceSource && typeof priceSource.peekFuturePrices === "function"
    );
  }, [priceSource]);

  useEffect(() => {
    if (!isActive || !priceSource || !canPeek) return;

    const interval = setInterval(() => {
      const now = performance.now();
      const gapMs = now - lastShownAtRef.current;
      if (
        lastShownAtRef.current !== 0 &&
        gapMs < MIN_GAP_BETWEEN_NEWS_SEC * 1000
      )
        return;

      const current = priceSource.peekCurrent(currentPriceRef.current);
      const future = priceSource.peekFuturePrices(
        Math.ceil(MAX_TIME_WINDOW_SEC * pointsPerSecond)
      );

      const spike = detectSpike(current, future, pointsPerSecond);
      let predictedDirection: Direction | null = spike?.direction ?? null;

      // Fallback: if no spike recently, emit a soft-confidence news leaning to latest drift
      if (!predictedDirection && gapMs >= MAX_GAP_WITHOUT_NEWS_SEC * 1000) {
        const lastFuture = future[future.length - 1];
        if (isFinite(lastFuture)) {
          predictedDirection = lastFuture >= current ? "up" : "down";
        }
      }
      if (!predictedDirection) return;

      // Decide whether the message is correct or fake according to quote confidence
      // We first choose the message (with its own confidence), then flip direction with probability (1 - confidence)
      const truthfulDirection = predictedDirection;
      let msg = buildNewsMessage(truthfulDirection);
      const isTruthful = Math.random() <= msg.confidencePct / 100;
      if (!isTruthful) {
        // Flip direction by picking a quote for the opposite side, keeping same confidence style distribution
        const flippedDirection: Direction =
          truthfulDirection === "up" ? "down" : "up";
        msg = buildNewsMessage(flippedDirection);
      }

      lastShownAtRef.current = now;
      setMessage(msg);
      // Restart scrolling animation
      setAnimKey((k) => (k + 1) % 1000000);
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isActive, priceSource, pointsPerSecond, canPeek]);

  if (!isActive || !message) return null;

  return (
    <div className="news-ticker" aria-live="polite" aria-atomic="true">
      <div
        key={animKey}
        className={`news-ticker__strip news-ticker__strip--${message.direction}`}
        style={{ animationDuration: `${SCROLL_DURATION_MS}ms` }}
      >
        <span className="news-ticker__name">{message.name}:</span>
        <span className="news-ticker__quote">“{message.text}”</span>
        <span className="news-ticker__meta">
          {message.confidencePct}% {message.direction === "up" ? "UP" : "DOWN"}
        </span>
      </div>
    </div>
  );
}

export default NewsTicker;
